import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { publicApi } from '@/utils/api';
import { queryClient } from '@/utils/query';

export type UseActiveWorkflowsResponse = {
  pipelines: string[];
  workflows: string[];
};

const useAppWorkflowsForFilter = ({
  appSlug,
  withPipelineWorkflows,
}: {
  appSlug?: string;
  withPipelineWorkflows: boolean;
}) => {
  const queryKey = useCallback((keyword: string = '') => [appSlug, keyword], [appSlug]);
  const queryFn = useCallback(
    (keyword: string = '') =>
      async () => {
        const response = await publicApi.get<{
          data?: {
            activeWorkflows: {
              item: {
                name: string;
              };
            }[];
            activePipelines: {
              item: {
                name: string;
              };
            }[];
          };
        }>(`/v0.1/apps/${appSlug}/pipelines/active-pipelineables`, {
          params: { with_pipeline_workflows: withPipelineWorkflows, pipelineable: keyword },
        });

        return {
          workflows: response?.data?.data?.activeWorkflows?.map((item: any) => item.name as string) ?? [],
          pipelines: response?.data?.data?.activePipelines?.map((item: any) => item.name as string) ?? [],
        };
      },

    [appSlug, withPipelineWorkflows],
  );

  const { data } = useQuery<unknown, unknown, UseActiveWorkflowsResponse>({
    queryKey: queryKey(),
    queryFn: queryFn(),
    retry: false,
  });

  const fetchQuery = useCallback(
    (keyword: string) => {
      return queryClient.fetchQuery({
        queryKey: queryKey(keyword),
        queryFn: queryFn(keyword),
        retry: false,
      });
    },
    [queryFn, queryKey],
  );

  return {
    data,
    fetchQuery,
  };
};

export default useAppWorkflowsForFilter;
